import { Badge, Icon, Text, Heading, LoadingSpinner } from "@oysterhr/ui"

import { useTranslation } from "react-i18next"
import { CorePaymentMethod, PaymentMethodState } from "@oysterhr/schema"
import { PaymentMethodEnumType } from "~/app/utils/constants"
import { BadgeStateItem } from "~/shared/components/StatesDescription"

const PaymentMethodStateMap: Record<PaymentMethodState, BadgeStateItem> = {
  [PaymentMethodState.Verified]: {
    translationKey: "state.pendingAuthorization",
    descriptionKey: "",
    variation: "warning",
  },
  [PaymentMethodState.InVerification]: {
    translationKey: "state.pendingVerification",
    descriptionKey: "",
    variation: "warning",
  },
  [PaymentMethodState.AuthorizedForDirectDebit]: {
    translationKey: "state.ready",
    descriptionKey: "",
    variation: "success",
  },
  [PaymentMethodState.AuthorizedByUser]: {
    translationKey: "state.authorizedByUser",
    descriptionKey: "",
    variation: "warning",
  },
  [PaymentMethodState.Initialized]: {
    translationKey: "state.pendingAuthorization",
    descriptionKey: "",
    variation: "warning",
  },
  [PaymentMethodState.Verifying]: {
    translationKey: "state.verifying",
    descriptionKey: "",
    variation: "warning",
  },
  [PaymentMethodState.VerificationFailed]: {
    translationKey: "state.verificationFailed",
    descriptionKey: "",
    variation: "error",
  },
}

const setupNameMap = {
  [PaymentMethodEnumType.STRIPE_SEPA]: "SEPA",
  [PaymentMethodEnumType.STRIPE_BACS]: "BACS",
  [PaymentMethodEnumType.STRIPE_ACH]: "ACH",
  [PaymentMethodEnumType.STRIPE_PAD]: "PAD",
}

interface CompanyPaymentMethodCardLayoutProps {
  paymentMethod: CorePaymentMethod
  stateLoading: boolean
  isDeactivated: boolean
  children: React.ReactNode
}

export const CompanyPaymentMethodCardLayout = ({
  paymentMethod,
  stateLoading,
  isDeactivated,
  children,
}: CompanyPaymentMethodCardLayoutProps) => {
  const { t } = useTranslation("company_interface/CompanyPaymentMethodsPage/CardLayout")

  const routingTitle =
    paymentMethod.__typename === PaymentMethodEnumType.STRIPE_SEPA
      ? t("routing.bankCode")
      : t("routing.routingCode")
  const { routingNumber, accountNumber, currencyCode } = paymentMethod

  return (
    <div
      data-testid={paymentMethod.id}
      className="relative h-full flex-col justify-between rounded-lg border border-gray-100 p-4"
    >
      <div className="flex items-center justify-between">
        <div className="flex">
          <Icon className="text-black" size="sm" name={"BankIcon"} />
          <Text className="ml-2 whitespace-nowrap font-semibold">{t("bankAccount")}</Text>
        </div>
        <div>
          {stateLoading ? (
            <LoadingSpinner />
          ) : (
            <Badge
              size="md"
              variation={
                isDeactivated ? "error" : PaymentMethodStateMap[paymentMethod.state].variation
              }
            >
              {isDeactivated ? (
                <span className="text-error-base">{t("state.deactivated")}</span>
              ) : (
                t(PaymentMethodStateMap[paymentMethod.state].translationKey)
              )}
            </Badge>
          )}
        </div>
      </div>

      <div className="w-2/3">
        <div className="mt-8 flex gap-8">
          <div className="flex flex-col">
            <Text className="text-gray-400" size="small">
              {t("currency")}
            </Text>
            <Heading as="h4">{currencyCode}</Heading>
          </div>
          <div className="flex flex-col">
            <Text className="whitespace-nowrap text-gray-400" size="small">
              {t("accountNumber")}
            </Text>
            <Heading as="h4">
              {accountNumber?.substring(accountNumber.length - 6, accountNumber.length)}
            </Heading>
          </div>
          <div className="flex flex-col">
            <Text className="whitespace-nowrap text-gray-400" size="small">
              {routingTitle}
            </Text>
            <Heading as="h4">{routingNumber}</Heading>
          </div>
        </div>
      </div>
      <div className="mt-6 flex items-center justify-between">
        <Text className="text-gray-400" size="small">
          {paymentMethod.__typename && setupNameMap[paymentMethod.__typename]} setup
        </Text>
        {children}
      </div>
    </div>
  )
}
